import {useContext, useMemo, useRef, useState} from 'react'

import cx from 'classnames'
import PropTypes from 'prop-types'

import {atomButtonShapes} from '@s-ui/react-atom-button'
import Context from '@s-ui/react-context'
import MoleculeSelectPopover, {
  selectPopoverOverlayTypes,
  selectPopoverShapes
} from '@s-ui/react-molecule-select-popover'
import Injector from '@s-ui/react-primitive-injector'
import ChevronDown from '@adv-mt/mt-svg-icons/lib/ChevronDown.js'
import ChevronRight from '@adv-mt/mt-svg-icons/lib/ChevronRight.js'
import IconClose from '@adv-mt/mt-svg-icons/lib/Close.js'

import {
  SELECT_POPOVER_CLASS_NAME,
  SELECT_POPOVER_CONTENT_CLASS_NAME,
  SELECT_POPOVER_POSITIONS,
  TYPES,
  WRAPPERS
} from '../../config.js'
import FilterWrapperBasicLabelWrapper from '../LabelWrapper/index.js'
import SelectPopoverModalContentWrapper from '../SelectPopoverModalContentWrapper.js'
import {SELECT_POPOVER_ID_PREFIX} from './config.js'

const FilterWrapperBasicSelectPopover = ({
  autoclose,
  description,
  disabled,
  filters,
  group,
  id,
  label,
  nodeLabel,
  onChange,
  onReset,
  overlayContentRef,
  placeholder,
  position = SELECT_POPOVER_POSITIONS.RIGHT,
  scrollIntoView = false,
  shape,
  showLabel,
  value,
  withRemoveSelections,
  withSearchBar,
  wrapper = WRAPPERS.NONE
}) => {
  const [forceClose, setForceClose] = useState(false)

  const {i18n} = useContext(Context)

  const popoverRef = useRef()
  const popoverContentRef = useRef()

  const Wrapper = WRAPPERS[wrapper]

  const isRightPopoverPosition = position === SELECT_POPOVER_POSITIONS.RIGHT
  const isModalPopoverPosition = position === SELECT_POPOVER_POSITIONS.MODAL

  const baseClassNames = cx(SELECT_POPOVER_CLASS_NAME, `${SELECT_POPOVER_CLASS_NAME}--${position}`)

  const contentClassNames = cx(SELECT_POPOVER_CONTENT_CLASS_NAME, {
    [`${SELECT_POPOVER_CONTENT_CLASS_NAME}--fullHeightVisibleScroll`]: isModalPopoverPosition,
    [`${SELECT_POPOVER_CONTENT_CLASS_NAME}--noFlex`]: wrapper === TYPES.TABS
  })

  const hasSelections = useMemo(() => {
    const allSelectionsIds = filters.map(({allSelectionsId}) => allSelectionsId)
    return Object.values(value).some(([id]) => !allSelectionsIds.includes(id))
  }, [filters, value])

  const handleChange = ({group, name, value}) => {
    autoclose && setForceClose(true)
    onChange({group, name, value})
  }

  const handleOpenFilter = () => {
    autoclose && setForceClose(false)
    scrollIntoView && popoverContentRef.current.scrollIntoView({behavior: 'smooth', block: 'center'})
  }

  const handleCloseFilter = () => {
    scrollIntoView && popoverRef.current.scrollIntoView({behavior: 'smooth', block: 'center'})
  }

  const handleResetFilter = () => {
    onReset({group, filters: filters.map(({name}) => name)})
  }

  return (
    <div className={baseClassNames} ref={popoverRef}>
      <FilterWrapperBasicLabelWrapper
        description={description}
        label={label}
        nodeLabel={nodeLabel}
        showLabel={showLabel}
      />
      <MoleculeSelectPopover
        acceptButtonOptions={{shape: atomButtonShapes.CIRCULAR}}
        acceptButtonText={i18n.t('COMMON.ACCEPT')}
        cancelButtonOptions={{shape: atomButtonShapes.CIRCULAR}}
        cancelButtonText={i18n.t('COMMON.CANCEL')}
        forceClosePopover={forceClose}
        fullWidth
        iconArrowDown={isRightPopoverPosition ? ChevronRight : ChevronDown}
        id={`${SELECT_POPOVER_ID_PREFIX}${id}`}
        isDisabled={disabled}
        isSelected={hasSelections}
        onClose={handleCloseFilter}
        onOpen={handleOpenFilter}
        overlayContentRef={overlayContentRef}
        overlayType={selectPopoverOverlayTypes.LIGHT}
        renderContentWrapper={isModalPopoverPosition && SelectPopoverModalContentWrapper}
        selectText={placeholder}
        shape={shape}
        {...(withRemoveSelections && {
          removeButtonOptions: {
            isShown: hasSelections,
            onClick: handleResetFilter,
            rightIcon: IconClose,
            shape: atomButtonShapes.SQUARED
          }
        })}
      >
        <div className={contentClassNames} ref={popoverContentRef}>
          <Injector filters={filters} group={group} onChange={handleChange} value={value} withSearchBar={withSearchBar}>
            {Wrapper}
          </Injector>
        </div>
      </MoleculeSelectPopover>
    </div>
  )
}

FilterWrapperBasicSelectPopover.displayName = 'FilterWrapperBasicSelectPopover'
FilterWrapperBasicSelectPopover.propTypes = {
  autoclose: PropTypes.bool,
  description: PropTypes.string,
  disabled: PropTypes.bool,
  filters: PropTypes.arrayOf(
    PropTypes.shape({
      allSelectionsId: PropTypes.string,
      allSelectionsLabel: PropTypes.string,
      label: PropTypes.string,
      name: PropTypes.string,
      options: PropTypes.arrayOf(
        PropTypes.shape({
          extendedInfo: PropTypes.string,
          icon: PropTypes.node,
          id: PropTypes.string,
          isHighlighted: PropTypes.bool,
          label: PropTypes.string,
          nestedItems: PropTypes.arrayOf(
            PropTypes.shape({
              extendedInfo: PropTypes.string,
              icon: PropTypes.node,
              id: PropTypes.string,
              isHighlighted: PropTypes.bool,
              label: PropTypes.string
            })
          )
        })
      )
    })
  ),
  group: PropTypes.string,
  id: PropTypes.string,
  label: PropTypes.string,
  nodeLabel: PropTypes.node,
  onChange: PropTypes.func,
  onReset: PropTypes.func,
  overlayContentRef: PropTypes.object,
  placeholder: PropTypes.string,
  position: PropTypes.string,
  scrollIntoView: PropTypes.bool,
  shape: PropTypes.oneOf(Object.values(selectPopoverShapes)),
  showLabel: PropTypes.bool,
  value: PropTypes.object,
  withRemoveSelections: PropTypes.bool,
  withSearchBar: PropTypes.bool,
  wrapper: PropTypes.string
}

export default FilterWrapperBasicSelectPopover
